import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Coins, ChevronRight, ChevronLeft, Gift, CreditCard, Loader2, CheckCircle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../api';
import type { BillingLedgerItem, BillingRechargeOrderRequest, BillingRechargeSku } from '../api';
import Modal from './Modal';
import { toast } from './Toast';
import zfbicon from '../img/zfb-app.png'
import wxicon from  '../img/wx-app.png'

function formatTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 明细分类筛选：direction 参数 1 全部 / 2 获得 / 3 消耗 */
const directionTabs: { value: 1 | 2 | 3; label: string }[] = [
  { value: 1, label: '全部' },
  { value: 2, label: '获得' },
  { value: 3, label: '消耗' },
];

const ALIPAY_ICON = zfbicon;
const WECHAT_ICON = wxicon ;

/** 充值汇率：1 元 = 10 橡树果 */
const ACORNS_PER_YUAN = 10;

/** 本地暂存未完成的充值订单（订单号 + 充值/赠送数量），支付宝回跳后据此查询支付结果 */
export const PENDING_ORDER_KEY = 'odance_pending_recharge_order';

/** 将后端返回的支付表单注入页面并自动提交（支付宝跳转） */
function submitPaymentForm(formHtml: string): void {
  const container = document.createElement('div');
  container.style.display = 'none';
  container.innerHTML = formHtml;
  document.body.appendChild(container);
  const form = container.querySelector('form');
  if (form) {
    (form as HTMLFormElement).submit();
  } else {
    console.warn('支付表单为空，无法提交');
  }
}

/** Header 橡树果余额入口：点击展开充值弹框（余额 + 明细 + 充值数量选择） */
export default function BalanceDisplay() {
  const [balance, setBalance] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [showLedger, setShowLedger] = useState(false);
  const [skus, setSkus] = useState<BillingRechargeSku[]>([]);
  const [selectedSkuId, setSelectedSkuId] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [ledger, setLedger] = useState<BillingLedgerItem[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [direction, setDirection] = useState<1 | 2 | 3>(1);
  const [paymentMethod, setPaymentMethod] = useState<'alipay' | 'wechat'>('alipay');
  const [submitting, setSubmitting] = useState(false);
  const [successInfo, setSuccessInfo] = useState<{ rechargeQuantity: number; giftQuantity: number; balance: number } | null>(null);
  const [wechatPay, setWechatPay] = useState<{ paymentOrderId: string; codeUrl: string; rechargeQuantity: number; giftQuantity: number } | null>(null);

  const loadBalance = useCallback(async () => {
    try {
      const res = await api.billing.getAccount();
      setBalance(res.availableAmount);
    } catch (error) {
      console.error('Load billing account failed:', error);
    }
  }, []);

  const loadSkus = useCallback(async () => {
    try {
      const list = await api.billing.getRechargeSku({ platform: 2 });
      setSkus(list);
    } catch (error) {
      console.error('Load recharge sku failed:', error);
    }
  }, []);

  const checkPendingPayment = useCallback(async () => {
    const raw = localStorage.getItem(PENDING_ORDER_KEY);
    if (!raw) return;
    // 先移除，避免 StrictMode 双触发导致重复查询
    localStorage.removeItem(PENDING_ORDER_KEY);
    let pending: { paymentOrderId: string; rechargeQuantity: number; giftQuantity: number };
    try {
      pending = JSON.parse(raw);
    } catch {
      return;
    }
    try {
      const result = await api.billing.getPaymentResult(pending.paymentOrderId);
      // 4 = 已充值到账
      if (result.paymentOrderStatus === 4) {
        const account = await api.billing.getAccount();
        setBalance(account.availableAmount);
        setSuccessInfo({
          rechargeQuantity: pending.rechargeQuantity,
          giftQuantity: pending.giftQuantity,
          balance: account.availableAmount,
        });
      }
    } catch (error) {
      console.error('Query payment result failed:', error);
    }
  }, []);

  useEffect(() => {
    loadBalance();
    checkPendingPayment();
  }, [loadBalance, checkPendingPayment]);

  // 微信 NATIVE：展示二维码后轮询支付结果，4 = 已充值到账
  useEffect(() => {
    if (!wechatPay) return;
    const timer = setInterval(async () => {
      try {
        const result = await api.billing.getPaymentResult(wechatPay.paymentOrderId);
        if (result.paymentOrderStatus === 4) {
          clearInterval(timer);
          setWechatPay(null);
          localStorage.removeItem(PENDING_ORDER_KEY);
          const account = await api.billing.getAccount();
          setBalance(account.availableAmount);
          setSuccessInfo({
            rechargeQuantity: wechatPay.rechargeQuantity,
            giftQuantity: wechatPay.giftQuantity,
            balance: account.availableAmount,
          });
        }
      } catch (error) {
        console.error('Query wechat payment result failed:', error);
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [wechatPay]);

  const openModal = () => {
    setOpen(true);
    setShowLedger(false);
    setSelectedSkuId(null);
    setCustomAmount('');
    loadBalance();
    loadSkus();
  };

  const closeModal = () => setOpen(false);

  const loadLedger = useCallback(async (dir: 1 | 2 | 3) => {
    setLedgerLoading(true);
    try {
      const items = await api.billing.getLedger({ pageNo: 1, pageSize: 20, direction: dir });
      setLedger(items);
    } catch (error) {
      console.error('Load ledger failed:', error);
      setLedger([]);
    } finally {
      setLedgerLoading(false);
    }
  }, []);

  const openLedger = () => {
    setShowLedger(true);
    setDirection(1);
    loadLedger(1);
  };

  const changeDirection = (dir: 1 | 2 | 3) => {
    setDirection(dir);
    loadLedger(dir);
  };

  const handlePay = async () => {
    const sku = skus.find((s) => s.skuId === selectedSkuId);
    const isFixed = !!selectedSkuId && !!sku;
    const customNum = Number(customAmount);

    if (!isFixed && (!customAmount || customNum < 1)) {
      toast('warning', '请选择充值数量或输入其他金额');
      return;
    }

    setSubmitting(true);
    try {
      const payload: BillingRechargeOrderRequest = isFixed && sku
        ? {
            paymentProvider: paymentMethod === 'alipay' ? 1 : 2, // 1 支付宝 / 2 微信
            rechargeType: 1, // 固定 SKU
            rechargeAmount: sku.cashAmount,
            platform: 3, // WEB
            skuId: sku.skuId,
          }
        : {
            paymentProvider: paymentMethod === 'alipay' ? 1 : 2,
            rechargeType: 2, // 自定义金额
            rechargeAmount: customNum,
            platform: 3,
          };

      const res = await api.billing.createRechargeOrder(payload);
      // 把订单号与本次充值/赠送数量存到本地，支付回跳/刷新后据此查询支付结果
      const rechargeQuantity = isFixed && sku ? sku.virtualBaseAmount : customNum * ACORNS_PER_YUAN;
      const giftQuantity = isFixed && sku ? sku.virtualGiftAmount : 0;
      localStorage.setItem(PENDING_ORDER_KEY, JSON.stringify({
        paymentOrderId: res.paymentOrderId,
        rechargeQuantity,
        giftQuantity,
      }));

      if (paymentMethod === 'wechat') {
        // 微信 NATIVE：弹二维码，由前端轮询支付结果
        setWechatPay({
          paymentOrderId: res.paymentOrderId,
          codeUrl: res.paymentParams.codeUrl ?? '',
          rechargeQuantity,
          giftQuantity,
        });
        setOpen(false);
      } else {
        // 支付宝：表单自动提交跳转
        const formHtml = res.paymentParams.pageRedirectionData;
        console.log('支付表单:', formHtml);
        submitPaymentForm(formHtml);
      }
    } catch (error) {
      console.error('Create recharge order failed:', error);
      toast('error', '创建订单失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const closeWechatPay = () => {
    setWechatPay(null);
    localStorage.removeItem(PENDING_ORDER_KEY);
  };

  const selectSku = (skuId: string) => {
    setSelectedSkuId(skuId);
    setCustomAmount('');
  };

  const handleCustomChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setCustomAmount(value);
    setSelectedSkuId(null);
  };

  // 自定义金额换算：1 元 = ACORNS_PER_YUAN 橡树果
  const customAcorns = Number(customAmount) * ACORNS_PER_YUAN;

  return (
    <>
      {/* 余额入口按钮 */}
      <button
        onClick={openModal}
        className="h-10 px-3.5 flex items-center gap-1.5 rounded-xl bg-white border border-border-subtle shadow-soft shrink-0 hover:border-brand/40 transition-colors"
      >
        <Coins size={16} className="text-warning" />
        <span className="text-[13px] font-semibold text-text-primary tabular-nums">
          {balance === null ? '--' : balance}
        </span>
      </button>

      <Modal isOpen={open} onClose={closeModal} title={showLedger ? '橡树果明细' : '橡树果充值'} width="max-w-sm">
        {showLedger ? (
          <div>
            {/* 分类筛选 */}
            <div className="flex gap-1.5 mb-3">
              {directionTabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => changeDirection(tab.value)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    direction === tab.value ? 'bg-brand text-white' : 'bg-slate-100 text-text-secondary hover:bg-slate-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {ledgerLoading ? (
              <div className="py-10 text-center text-xs text-text-muted">加载中...</div>
            ) : ledger.length > 0 ? (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {ledger.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 border-b border-border-subtle last:border-0">
                    <div>
                      <p className="text-[13px] text-text-primary">{item.title}</p>
                      <p className="text-[11px] text-text-muted mt-0.5">{formatTime(item.createdAt)}</p>
                    </div>
                    <span className={`text-[13px] font-semibold ${item.direction === 'income' ? 'text-success' : 'text-text-primary'}`}>
                      {item.direction === 'income' ? '+' : '-'}{item.amount}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-10 text-center text-xs text-text-muted">暂无明细记录</div>
            )}
            <button
              onClick={() => setShowLedger(false)}
              className="mt-3 w-full flex items-center justify-center gap-1 py-2 text-[13px] text-brand font-medium hover:bg-brand-50 rounded-xl transition-colors"
            >
              <ChevronLeft size={15} /> 返回充值
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* 当前余额 + 查看明细 */}
            <div className="flex items-center justify-between p-4 rounded-2xl bg-brand-50">
              <div>
                <p className="text-xs text-text-secondary mb-1">当前余额</p>
                <p className="text-[22px] font-bold text-text-primary leading-none flex items-baseline gap-1">
                  <Coins size={18} className="text-warning self-center" />
                  {balance === null ? '--' : balance}
                  <span className="text-xs font-normal text-text-muted">橡树果</span>
                </p>
              </div>
              <button onClick={openLedger} className="flex items-center gap-0.5 text-[12px] text-brand font-medium hover:underline shrink-0">
                查看明细 <ChevronRight size={14} />
              </button>
            </div>

            {/* 选择充值数量 */}
            <div>
              <p className="text-sm font-semibold text-text-primary mb-2">选择充值数量</p>
              {skus.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {skus.map((sku) => {
                    const active = selectedSkuId === sku.skuId;
                    return (
                      <button
                        key={sku.skuId}
                        onClick={() => selectSku(sku.skuId)}
                        className={`relative rounded-xl border py-2.5 flex flex-col items-center transition-all ${
                          active ? 'border-brand bg-brand-50' : 'border-border-main hover:border-brand/40'
                        }`}
                      >
                        <span className={`text-[15px] font-bold ${active ? 'text-brand-dark' : 'text-text-primary'}`}>
                          ¥{sku.cashAmount}
                        </span>
                        <span className="text-[11px] text-text-muted mt-0.5">
                          {sku.virtualBaseAmount} 果
                        </span>
                        {sku.virtualGiftAmount > 0 ? (
                          <span className="flex items-center gap-0.5 text-[10px] text-warning mt-0.5">
                            <Gift size={11} /> 送 {sku.virtualGiftAmount} 果
                          </span>
                        ) : (
                          <span className="text-[10px] text-text-muted mt-0.5">无赠送</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-text-muted">暂无可选档位</p>
              )}
            </div>

            {/* 其他金额 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-text-primary">其他金额</p>
                <span className="text-xs text-text-muted">1元={ACORNS_PER_YUAN}橡树果</span>
              </div>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  value={customAmount}
                  onChange={handleCustomChange}
                  placeholder="请输入金额"
                  maxLength={4}
                  className="w-full px-4 py-3 pr-14 bg-white border border-border-main rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-all"
                />
                <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-text-muted">元</span>
              </div>
              {customAcorns > 0 && (
                <p className="mt-2 text-xs text-text-muted">可获得 {customAcorns} 橡树果</p>
              )}
            </div>

            {/* 支付方式 */}
            <div>
              <p className="text-sm font-semibold text-text-primary mb-2">支付方式</p>
              <div className="grid grid-cols-1 gap-2">
                <label
                  className={`flex items-center gap-2.5 px-3 py-3 border-2 rounded-xl cursor-pointer transition-all ${
                    paymentMethod === 'alipay' ? 'border-brand bg-brand-50/50' : 'border-border-main hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="pay"
                    checked={paymentMethod === 'alipay'}
                    onChange={() => setPaymentMethod('alipay')}
                    className="text-brand w-4 h-4"
                  />
                  <img src={ALIPAY_ICON} alt="支付宝" className="w-6 h-6 object-contain" />
                  <span className="text-sm font-medium text-text-primary">支付宝</span>
                  <span className="ml-auto shrink-0 px-1.5 py-0.5 rounded bg-brand text-white text-[10px] font-medium">推荐</span>
                </label>
                <label
                  className={`flex items-center gap-2.5 px-3 py-3 border-2 rounded-xl cursor-pointer transition-all ${
                    paymentMethod === 'wechat' ? 'border-brand bg-brand-50/50' : 'border-border-main hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="pay"
                    checked={paymentMethod === 'wechat'}
                    onChange={() => setPaymentMethod('wechat')}
                    className="text-brand w-4 h-4"
                  />
                  <img src={WECHAT_ICON} alt="微信" className="w-6 h-6 object-contain" />
                  <span className="text-sm font-medium text-text-primary">微信支付</span>
                  <span className="ml-auto shrink-0 px-1.5 py-0.5 rounded bg-success text-white text-[10px] font-medium">扫码付</span>
                </label>
              </div>
            </div>

            {/* 立即支付 */}
            <button
              onClick={handlePay}
              disabled={submitting}
              className="w-full py-3 gradient-brand text-white rounded-xl text-sm font-semibold hover-lift disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
              {submitting ? '提交中...' : '立即支付'}
            </button>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!wechatPay} onClose={closeWechatPay} title="微信扫码支付" width="max-w-xs" hideClose>
        {wechatPay && (
          <div className="text-center py-2">
            <p className="text-sm text-text-secondary mb-4">请使用微信扫码支付</p>
            <div className="inline-block bg-white rounded-xl p-4 border border-border-subtle">
              <QRCodeSVG value={wechatPay.codeUrl} size={200} marginSize={4} />
            </div>
            <p className="text-xs text-text-muted mt-4">支付完成后将自动更新余额</p>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!successInfo} onClose={() => setSuccessInfo(null)} title="充值成功" width="max-w-sm" hideClose>
        {successInfo && (
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-success-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={40} className="text-success" />
            </div>
            <h3 className="text-lg font-bold text-text-primary mb-4 font-display">充值成功！</h3>
            <div className="space-y-2 text-left">
              <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                <span className="text-xs text-text-muted">充值数量</span>
                <span className="text-sm text-text-primary font-medium">{successInfo.rechargeQuantity} 果</span>
              </div>
              <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                <span className="text-xs text-text-muted">赠送数量</span>
                <span className="text-sm text-text-primary font-medium">{successInfo.giftQuantity} 果</span>
              </div>
              <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                <span className="text-xs text-text-muted">当前余额</span>
                <span className="text-sm text-text-primary font-medium">{successInfo.balance} 果</span>
              </div>
            </div>
            <button onClick={() => setSuccessInfo(null)} className="mt-6 px-8 py-3 gradient-brand text-white rounded-xl text-sm font-semibold hover-lift">
              确定
            </button>
          </div>
        )}
      </Modal>
    </>
  );
}
